import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Button, Tooltip } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { fetchDriveItemContent } from "../../services/drive.service";
import { exportFactFind } from "../../services/factfind.service";

// ── Helpers ──────────────────────────────────────────────────────────────────

const camelToTitle = (key: string): string =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

const esc = (s: string): string =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const TH = `style="background:#e8f4fd;padding:8px 12px;text-align:left;font-weight:bold;border:1px solid #d4d4d4;font-size:8.5pt;color:#1a4c7a"`;
const tdStyle = (even: boolean) =>
  `style="padding:6px 12px;border:1px solid #d4d4d4;font-size:9pt;background:${even ? "#f9f9f9" : "#fff"}"`;

function renderObject(obj: Record<string, any>, depth = 0): string {
  const entries = Object.entries(obj || {}).filter(([k]) => k !== "_id");
  if (entries.length === 0) return `<p style="color:#999;font-size:9pt">No data</p>`;

  const allPrimitive = entries.every(([, v]) => typeof v !== "object" || v === null);

  if (allPrimitive) {
    let html = `<table style="border-collapse:collapse;width:100%;margin:0 0 12px">`;
    html += `<tr><th ${TH} width="40%">Field</th><th ${TH} width="60%">Value</th></tr>`;
    entries.forEach(([k, v], idx) => {
      const raw = String(v ?? "").trim();
      const val = raw !== "" && raw.toLowerCase() !== "n/a" ? esc(raw) : "—";
      html += `<tr><td ${tdStyle(idx % 2 !== 0)}>${esc(camelToTitle(k))}</td><td ${tdStyle(idx % 2 !== 0)}>${val}</td></tr>`;
    });
    html += "</table>";
    return html;
  }

  let html = "";
  entries.forEach(([k, v]) => {
    const label = camelToTitle(k);
    const tag = depth === 0 ? "h2" : "h3";
    const color = depth === 0 ? "#1976d2" : "#444444";
    const fontSize = depth === 0 ? "11pt" : "10pt";
    const style = depth === 0
      ? `style="color:${color};font-size:${fontSize};margin:14px 0 6px"`
      : `style="color:${color};font-size:${fontSize};font-style:italic;margin:10px 0 4px"`;
    html += `<${tag} ${style}>${esc(label)}</${tag}>`;

    if (Array.isArray(v)) {
      if (v.length === 0) {
        html += `<p style="color:#999;font-size:9pt">None</p>`;
      } else {
        v.forEach((item, i) => {
          if (typeof item === "object" && item !== null) {
            html += `<p style="font-style:italic;font-size:10pt;color:#444444;margin:8px 0 4px">Item ${i + 1}</p>`;
            html += renderObject(item, depth + 1);
          }
        });
      }
    } else if (typeof v === "object" && v !== null) {
      html += renderObject(v, depth + 1);
    } else {
      const val = v !== null && v !== undefined && String(v).trim() !== "" ? esc(String(v)) : "—";
      html += `<p style="font-size:10pt;margin:4px 0 10px">${val}</p>`;
    }
  });
  return html;
}

function buildGraphHtml(graphData: any, clientName: string): string {
  const today = new Date().toLocaleDateString("en-AU", { day: "2-digit", month: "long", year: "numeric" });
  const tabs: any[] = graphData?.data || [];

  const iriToLabel = (iri: string): string => {
    const local = iri.includes("#") ? iri.split("#").pop()! : iri.split("/").pop()!;
    return local.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()).trim();
  };
  const TH2 = `style="background-color:#e8f4fd;padding:8px 12px;text-align:left;font-weight:bold;border:1px solid #d4d4d4;font-size:8.5pt;color:#1a4c7a"`;
  const TD2 = (even: boolean) => `style="padding:6px 12px;border:1px solid #d4d4d4;font-size:9pt;background-color:${even ? "#f9f9f9" : "#ffffff"}"`;

  let sections = "";
  tabs.forEach((t: any) => {
    sections += `<h1 style="color:#1565c0;border-bottom:1.5px solid #1565c0;padding-bottom:5px;font-size:13pt;margin-top:28px">${esc(t.tab || "Section")}</h1>`;
    (t.classes || []).forEach((cls: any) => {
      sections += `<h2 style="color:#1976d2;font-size:11pt;margin-top:16px;margin-bottom:6px">${esc(cls.label || iriToLabel(cls.class_iri || ""))}</h2>`;
      (cls.instances || []).forEach((inst: any, i: number) => {
        const instLabel = (inst.id || `Instance ${i + 1}`).replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
        sections += `<h3 style="color:#444444;font-size:10pt;font-style:italic;margin:10px 0 5px">${esc(instLabel)}</h3>`;
        const props = Object.entries(inst.properties ?? {}).filter(([propIri]: [string, any]) => {
          const local = propIri.includes("#") ? propIri.split("#").pop()! : propIri.split("/").pop()!;
          return !/id$/i.test(local);
        });
        if (props.length > 0) {
          sections += `<table style="border-collapse:collapse;width:100%;margin:0 0 14px">`;
          sections += `<tr><th ${TH2} width="40%">Property</th><th ${TH2} width="60%">Value</th></tr>`;
          props.forEach(([propIri, val]: [string, any], idx: number) => {
            const v = (val !== null && val !== undefined && String(val).trim() !== "") ? esc(String(val)) : "—";
            sections += `<tr><td ${TD2(idx % 2 !== 0)}>${esc(iriToLabel(propIri))}</td><td ${TD2(idx % 2 !== 0)}>${v}</td></tr>`;
          });
          sections += `</table>`;
        }
      });
    });
  });

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Fact Find Report</title>
<style>* { box-sizing: border-box; } body { font-family: "Palatino Linotype", "Book Antiqua", Palatino, Calibri, Arial, sans-serif; font-size: 10pt; color: #0a0a0a; max-width: 800px; margin: 0 auto; padding: 32px 40px; } @media print { .ff-toolbar { display: none !important; } }</style>
</head><body>
<h1 style="text-align:center;font-size:16pt;margin-bottom:4px">Fact Find Report</h1>
<p style="text-align:center;font-size:12pt;margin:4px 0">${esc(clientName)}</p>
<p style="text-align:center;font-style:italic;color:#666;margin:4px 0 16px">${esc(today)}</p>
<hr style="border:0;border-top:1.5px solid #333;margin:0 0 20px">
${sections}</body></html>`;
}

function buildFactFindHtml(factFindData: any, clientName: string): string {
  const today = new Date().toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  let sections = "";
  if (factFindData && typeof factFindData === "object") {
    Object.entries(factFindData).forEach(([sectionKey, sectionData]: [string, any]) => {
      sections += `<h1 style="color:#1565c0;border-bottom:1.5px solid #1565c0;padding-bottom:5px;font-size:13pt;margin-top:28px">${esc(camelToTitle(sectionKey))}</h1>`;
      if (Array.isArray(sectionData)) {
        if (sectionData.length === 0) {
          sections += `<p style="color:#999;font-size:9pt">None</p>`;
        } else {
          sectionData.forEach((item, i) => {
            sections += `<p style="font-style:italic;font-size:10pt;color:#444;margin:8px 0 4px">Item ${i + 1}</p>`;
            if (typeof item === "object" && item !== null) sections += renderObject(item, 1);
          });
        }
      } else if (typeof sectionData === "object" && sectionData !== null) {
        sections += renderObject(sectionData, 1);
      } else {
        sections += `<p>${esc(String(sectionData ?? "—"))}</p>`;
      }
    });
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Fact Find Report</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: "Palatino Linotype", "Book Antiqua", Palatino, Calibri, Arial, sans-serif; font-size: 10pt; color: #0a0a0a; max-width: 800px; margin: 0 auto; padding: 32px 40px; }
  @media print {
    body { padding: 16px 24px; }
    .ff-toolbar { display: none !important; }
  }
</style>
</head>
<body>
<h1 style="text-align:center;font-size:16pt;margin-bottom:4px">Fact Find Report</h1>
<p style="text-align:center;font-size:12pt;margin:4px 0">${esc(clientName)}</p>
<p style="text-align:center;font-style:italic;color:#666;margin:4px 0 16px">${esc(today)}</p>
<hr style="border:0;border-top:1.5px solid #333;margin:0 0 20px">
${sections}
</body>
</html>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface FactFindViewerProps {
  item: any;
}

export default function FactFindViewer({ item }: FactFindViewerProps) {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");
  const [clientGraphData, setClientGraphData] = useState<any>(null);
  const [fileName, setFileName] = useState<string>("");

  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true);
      setError("");
      try {
        const res: any = await fetchDriveItemContent(item._id);
        if (res?.content) {
          const { factFindData, clientGraphData: graphData, fileName: fn } = res.content;
          const namePart = (fn || item.name || "")
            .replace(/^\d{4}-\d{2}-\d{2}-FactFind-/, "")
            .replace(/\.(json|pdf)$/, "")
            .replace(/-/g, " ")
            .trim();
          setClientGraphData(graphData || null);
          setFileName(fn || item.name || "FactFind");
          const html = graphData?.data?.length
            ? buildGraphHtml(graphData, namePart || "Client")
            : buildFactFindHtml(factFindData, namePart || "Client");
          setHtmlContent(html);
        } else {
          throw new Error("No content returned.");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load fact find.");
      } finally {
        setIsLoading(false);
      }
    };
    if (item?._id) fetchContent();
  }, [item]);

  const handleDownloadPdf = async () => {
    if (!clientGraphData?.data?.length) return;
    setIsDownloading(true);
    try {
      const exportResp = await exportFactFind({
        graphData: clientGraphData,
        format: "pdf",
        fileName: fileName,
        clientName: fileName.replace(/^\d{4}-\d{2}-\d{2}-FactFind-/, "").replace(/-/g, " ").trim(),
      });
      if (!exportResp.ok) throw new Error("Export failed");
      const blob = await exportResp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: 2,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          bgcolor: "#fafafa",
          flexShrink: 0,
        }}
      >
        <Tooltip title="Open print dialog to save as PDF">
          <Button
            size="small"
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            disabled={isDownloading || !clientGraphData?.data?.length}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}
          >
            {isDownloading ? "Downloading…" : "Download PDF"}
          </Button>
        </Tooltip>
      </Box>

      {/* Rendered HTML report in iframe */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <iframe
          srcDoc={htmlContent}
          title="Fact Find Report"
          style={{ width: "100%", height: "100%", border: "none" }}
        />
      </Box>
    </Box>
  );
}
